/**
 * Water-quantity role required of a Generation screening document fact.
 *
 * A model-labelled "consumption" fact can quote a site allocation, a
 * whole-site average/peak demand, an allocation margin, a makeup/withdrawal
 * flow or a firm utility delivery. Those share a unit (gal/day, MGD, AFY,
 * gal/MWh) with consumptive capacity and cooling consumption but are different
 * quantities (River Ridge: a 75,000 gal/day allocation became "consumptive
 * capacity" and 75,000 - 55,000 = 20,000 gal/day became passing headroom).
 *
 * The deterministic authority is the role of the quoted number itself, read
 * by the shared classifier in src/lib/water/waterQuantityRoles.ts. The model's
 * free-text `basis` and qualification booleans are never sufficient.
 *
 * Shared by the extraction validator (new extractions) and the screening
 * prefill consumer (persisted facts), so both apply one table AND select the
 * same printed tokens (screeningWaterRoleTokens, built on the validator's own
 * source-quantity grammar, which lives here). Pure; no unit conversion;
 * UNKNOWN (null) never qualifies.
 */
import type { GenerationScreeningEvidenceField } from '../../types/generationImpact';
import {
  classifyWaterQuantityRole,
  type WaterQuantityRole,
} from '../water/waterQuantityRoles';

/** Roles a quoted number must carry for each water destination. */
export const SCREENING_WATER_FACT_ROLES = {
  /** Consumptive capacity: only a consumptive-use limit (entitlement, right, permitted/available consumptive use). */
  availableWaterConsumptionGalPerDay: ['consumptive-use-limit'],
  /** Facility cooling intensity: a process consumption or a consumptive-use limit, never a whole-site figure. */
  'water.dataCenterConsumptionGalPerMWhFacility': ['cooling-consumption', 'consumptive-use-limit'],
  'projectDuty.water.consumptionGalPerMWhGross': ['cooling-consumption', 'consumptive-use-limit'],
  /** Withdrawal intensity: withdrawal, makeup or intake only. */
  'projectDuty.water.withdrawalGalPerMWhGross': ['withdrawal-makeup'],
} as const satisfies Partial<Record<GenerationScreeningEvidenceField, readonly WaterQuantityRole[]>>;

export type ScreeningWaterFactField = keyof typeof SCREENING_WATER_FACT_ROLES;

export function isScreeningWaterFactField(field: string): field is ScreeningWaterFactField {
  return Object.prototype.hasOwnProperty.call(SCREENING_WATER_FACT_ROLES, field);
}

/**
 * The printed tokens of a water fact's own value, selected exactly as the
 * extraction validator selects them: tokens equal to the raw value (or, when
 * the raw value is not a printed number, the range's selected design value)
 * that sourceQuantityHasUnit binds to the raw unit. Another printing of the
 * same magnitude without that unit (a phase, exhibit, permit or MW number) is
 * not this fact's value. [] for a non-water field or a non-numeric value.
 *
 * Called by the validator for new facts and by prefill for persisted facts
 * (fact.raw.value, fact.raw.unit, fact.raw.range), so a fact the validator
 * found eligible is re-checked against the same tokens.
 */
export function screeningWaterRoleTokens(
  excerpt: string,
  rawValue: unknown,
  rawUnit: string | undefined,
  range: SourceRange | undefined,
  field: string,
): SourceNumericToken[] {
  if (!isScreeningWaterFactField(field)) return [];
  const value = strictNumber(rawValue) ?? strictNumber(range?.selectedDesignValue);
  if (value === undefined) return [];
  const tokens = numericSourceTokens(excerpt);
  return tokens.filter(token => token.value === value
    && sourceQuantityHasUnit(excerpt, token, tokens, rawUnit, range, field));
}

/**
 * True only when at least one token is supplied and every supplied token
 * classifies to a role accepted for `field`. Two printings of the same value
 * with different roles are ambiguous and fail closed.
 */
export function screeningWaterFactRoleIsBound(
  field: ScreeningWaterFactField,
  text: string,
  tokens: readonly { readonly start: number; readonly end: number }[],
): boolean {
  const accepted: readonly WaterQuantityRole[] = SCREENING_WATER_FACT_ROLES[field];
  return tokens.length > 0 && tokens.every(token => {
    const role = classifyWaterQuantityRole(text, token.start, token.end);
    return role !== null && accepted.includes(role);
  });
}

// ---------------------------------------------------------------------------
// Source-quantity grammar (moved unchanged from the extraction validator, which
// imports it from here, so the validator and prefill read one token grammar)
// ---------------------------------------------------------------------------

/** A printed numeric range as stored on a fact (`fact.raw.range`). */
export interface SourceRange { min: number; max: number; selectedDesignValue?: number }

/** A strictly numeric scalar (number, or a plain printed numeral string); no units applied. */
export function strictNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const cleaned = value.trim().replace(/,/g, '');
  if (!/^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i.test(cleaned)) return undefined;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** Exact printed numeric lexemes, with no arithmetic or scale conversion.
 * A hyphen between numeric endpoints is a range separator; an explicit minus
 * elsewhere remains negative. Commas, decimals and scientific notation keep
 * the same numerical value, while number/identifier substrings never count. */
export interface SourceNumericToken { value: number; start: number; end: number }
export function numericSourceTokens(excerpt: string): SourceNumericToken[] {
  const tokens: SourceNumericToken[] = [];
  const pattern = /[+-]?(?:(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?/gi;
  for (const match of excerpt.matchAll(pattern)) {
    let raw = match[0];
    let start = match.index!;
    const end = start + raw.length;
    const before = excerpt.slice(0, start);
    const rangeMinus = raw.startsWith('-') && /\d\s*$/.test(before);
    if (rangeMinus) { raw = raw.slice(1); start++; }
    const next = excerpt[end] ?? '';
    if (/[A-Za-z0-9_.,]/.test(excerpt[start - 1] ?? '')
      || /[0-9_]/.test(next)
      || (next === '.' && /\d/.test(excerpt[end + 1] ?? ''))
      || (next === ',' && /\d/.test(excerpt[end + 1] ?? ''))) continue;
    let value = strictNumber(raw);
    if (value === undefined) continue;
    // Also retain a separated/Unicode minus, unless it separates two printed
    // numeric endpoints. Do not reinterpret a negative value as positive.
    if (!/^[+-]/.test(raw)) {
      const sign = /([-−–—])\s*$/.exec(before);
      if (sign && !/\d\s*$/.test(before.slice(0, sign.index))) value = -value;
    }
    tokens.push({ value, start, end });
  }
  return tokens;
}

/** Lexical spellings of the same unit/scale only. These do not convert a
 * source quantity or add canonical destinations. In particular MGD vs gpd,
 * hour vs day, IT vs facility, gross vs facility and percent vs fraction stay
 * separate, and the natural gallon aliases explicitly retain US scope. */
function sourceUnitSpellings(rawUnit: string | undefined, field: string): readonly string[] {
  const groups: readonly (readonly string[])[] = [
    ['Dth/day', 'dekatherm/day'], ['MMBtu/day'], ['MMBtu/hour', 'MMBtu/hr', 'MMBtu/h'],
    ['gal/day', 'gallon/day', 'gallons/day', 'gpd'], ['MGD'],
    ['acre-foot/year', 'acre-feet/year', 'acre-ft/year', 'ac-ft/year', 'AFY'],
    ['metric-tonne/year', 'metric-tonnes/year', 'metric tonnes/year', 'tonne/year', 'tonnes/year', 't/year'],
    ['metric-tonne/day', 'metric-tonnes/day', 'metric tonnes/day', 'tonne/day', 'tonnes/day', 't/day'],
    ['gal/MWh-facility', 'gallon/MWh-facility'],
    ['US gallons per MWh of facility electricity', 'U.S. gallons per MWh of facility electricity',
      'US gallon per MWh of facility electricity', 'U.S. gallon per MWh of facility electricity',
      'US gallons per MWh of facility electrical load', 'U.S. gallons per MWh of facility electrical load',
      'US gallon per MWh of facility electrical load', 'U.S. gallon per MWh of facility electrical load'],
    ['L/kWh-IT', 'litre/kWh-IT', 'liter/kWh-IT'],
    ['m', 'metre', 'meter'], ['km', 'kilometre', 'kilometer'], ['ft', 'foot', 'feet'], ['mi', 'mile', 'miles'],
    ['count', 'fixture', 'fixtures', 'each', 'luminaire', 'luminaires'],
    ['W', 'watt', 'watts', 'W/fixture'], ['lm', 'lumen', 'lumens', 'lm/fixture'], ['MW'],
    ['degC', 'C', '°C', 'celsius'], ['fraction', 'ratio', 'p.u.', 'pu', 'factor'], ['%', 'percent', 'percentage'],
    ['Btu/kWh-HHV', 'Btu/kWh HHV'], ['Btu/kWh'], ['gal/MWh-gross', 'gallon/MWh-gross'],
    ['kWh/metric-tonne', 'kWh/metric tonnes', 'kWh/tonne', 'kWh/t'],
    ['gal/metric-tonne', 'gallon/metric-tonne', 'gal/tonne'],
    ['dBA-sound-power', 'dBA-re-1-pW', 'dBA'], ['dBA-reference-SPL', 'dBA-SPL', 'dBA'],
  ];
  if (!rawUnit?.trim()) return [];
  const unit = normalizeUnit(rawUnit);
  if (unit === 'dba' && field === 'projectDuty.acoustics.referenceSplDbA') {
    return ['dBA-reference-SPL', 'dBA-SPL', 'dBA'];
  }
  return groups.find(group => group.some(spelling => normalizeUnit(spelling) === unit)) ?? [rawUnit.trim()];
}

function sourceUnitPattern(spelling: string): string {
  return spelling.split(/(\s+|\/|-)/).map(part => {
    if (/^\s+$/.test(part)) return '\\s+';
    if (part === '/') return '\\s*(?:/|per)\\s*';
    if (part === '-') return '(?:-|\\s+)';
    return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }).join('');
}

/** Bind the scalar to its adjacent printed unit or an immediately preceding
 * unit label/header. A range may share its one trailing unit, only when both
 * exact stated endpoints match that range. Other numbers/units cannot help. */
export function sourceQuantityHasUnit(
  excerpt: string, token: SourceNumericToken, tokens: readonly SourceNumericToken[],
  rawUnit: string | undefined, range: { min: number; max: number } | undefined, field: string,
): boolean {
  const patterns = sourceUnitSpellings(rawUnit, field).map(sourceUnitPattern);
  const unitAfter = (end: number) => patterns.some(pattern =>
    new RegExp(`^\\s*\\(?\\s*(?:${pattern})(?![A-Za-z0-9_/-])`, 'i').test(excerpt.slice(end)));
  const unitBefore = (start: number) => patterns.some(pattern =>
    new RegExp(`(?:^|[^A-Za-z0-9_])(?:${pattern})(?![A-Za-z0-9_/-])\\s*\\)?\\s*(?::|=|is|of|equals)?\\s*$`, 'i')
      .test(excerpt.slice(0, start)));
  if (unitAfter(token.end) || unitBefore(token.start)) return true;
  if (!range) return false;
  const index = tokens.indexOf(token);
  const next = tokens[index + 1];
  const previous = tokens[index - 1];
  const joinsRange = (lower: SourceNumericToken, upper: SourceNumericToken) =>
    lower.value === range.min && upper.value === range.max
      && /^\s*(?:[-–—]|to)\s*$/i.test(excerpt.slice(lower.end, upper.start));
  return Boolean((next && joinsRange(token, next) && unitAfter(next.end))
    || (previous && joinsRange(previous, token) && unitBefore(previous.start)));
}

/** Lexical unit normalisation used to compare unit spellings; never converts a value. */
export function normalizeUnit(value: string | undefined): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[‐‑‒–—]/g, '-')
    .replace(/metric\s+tonnes?/g, 'metric-tonne')
    .replace(/acre\s+feet/g, 'acre-feet')
    .replace(/\s+per\s+/g, '/')
    .replace(/\s+/g, '')
    .replace(/\/yr$/, '/year')
    .replace(/\/d$/, '/day');
}
